<template>
    <cp-base-page
        :page-title="pageTitle"
        :category-subtitle="categorySubtitle"
    >
    <v-alert
        v-if="client && client.address_id === null"
        type="info"
        text
        class="mb-6"
    >
        Cliente sem endereço. Para emitir faturas, o cliente precisa ter um endereço cadastrado no sistema.
        <a
            :href="route('v2.clients.update-address', client.id)"
            class="text-primary"
            target="_blank"
        >
            Clique aqui para cadastrar
        </a>
    </v-alert>
        <template #actions-top v-if="showHeaderActions && createBillRouteName">
            <cp-button
                left-icon="$plus"
                small
                color="primary"
                elevation="0"
                link
                :href="createBillRoute"
                v-if="can('store bills')"
            >
            {{ createBillButtonText }}
            </cp-button>
        </template>
        <template #search-top>
            <cp-search
                v-if="billsData"
                :search="search"
                :params="paginationParams"
            />
        </template>
        <template #category-title>
            <v-row align-content="space-around">
                <v-col>
                    <div
                        class="t-heading-medium-lg"
                        v-text="listingTitle"
                    ></div>
                </v-col>
                <v-col
                    v-if="showFilters"
                    class="fill-height d-flex justify-end align-center"
                >
                    <div class="mr-5">
                        <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                                <cp-icon-button
                                    class="ml-3"
                                    icon="$filterOutline"
                                    :color="isFilterOpen ? 'primary' : 'secondary'"
                                    @click="isFilterOpen = !isFilterOpen"
                                    v-bind="attrs"
                                    v-on="on"
                                ></cp-icon-button>
                            </template>
                            <span>Filtros</span>
                        </v-tooltip>
                    </div>
                    <div class="mr-5" v-if="can('export bills')">
                        <v-tooltip bottom>
                            <template #activator="{ on, attrs }">
                                <cp-icon-button
                                    class="ml-3"
                                    icon="$download"
                                    color="secondary"
                                    @click="exportBills"
                                    v-bind="attrs"
                                    v-on="on"
                                ></cp-icon-button>
                            </template>
                            <span>Exportar</span>
                        </v-tooltip>
                    </div>

                    <v-btn-toggle v-model="status" borderless>
                        <v-btn
                            v-for="btn in statusButtons"
                            :key="btn.value"
                            :value="btn.value"
                            small
                            :active-class="btn.activeClass"
                            :href="route(indexRouteName, indexRouteParams(btn.param))"
                        >
                            <v-icon v-if="btn.icon" left>{{ btn.icon }}</v-icon>
                            <span class="hidden-sm-and-down">{{ btn.label }}</span>
                        </v-btn>
                    </v-btn-toggle>
                </v-col>
            </v-row>
        </template>

        <template #filters-top v-if="showFilters">
            <cp-active-filters-alert :filters="filters"/>
            <v-sheet
                v-if="isFilterOpen"
                outlined
                class="mt-3 pa-4"
            >
                <FilterBills
                    :filters="filters"
                    :search="search"
                />
            </v-sheet>
        </template>

        <template>
            <cp-clients v-if="client" :client="client"/>

            <v-data-table
                :style="{width: '100%' }"
                :headers="headers"
                :items="bills"
                item-key="id"
                mobile-breakpoint="700"
                no-data-text="Sem dados disponíveis"
                hide-default-footer
                :items-per-page="15"
            >
                <template v-slot:item.status="{ item }">
                    <v-chip
                        :color="item.statusData.color"
                        :text-color="item.statusData.textColor"
                        label
                        small
                    >
                        <v-icon small left>
                            {{ item.statusData.icon }}
                        </v-icon>
                        {{ item.statusData.text }}
                    </v-chip>
                </template>
                <template v-slot:item.company="{ item }">
                    <div v-if="item.company">
                        <cp-clients :client="item.company"/>
                    </div>
                </template>
                <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center">
                        <slot name="actions" :item="item" :client="client">
                            <v-tooltip left v-if="canView()">
                                <template v-slot:activator="{ on }">
                                    <div v-on="on">
                                        <cp-icon-button
                                            color="grey darken-2"
                                            title="Visualizar"
                                            icon="$eye"
                                            :href="item.url"
                                            target="_blank"
                                        />
                                    </div>
                                </template>
                                <span>
                                    Fatura
                                </span>
                            </v-tooltip>

                            <v-tooltip left v-if="canEdit(item)">
                                <template v-slot:activator="{ on }">
                                    <div v-on="on">
                                        <cp-icon-button
                                        title="Editar Fatura"
                                        icon="$edit"
                                        color="primary"
                                        :href="editBillRoute(billClientId(item), item.id)"
                                    />
                                </div>
                            </template>
                            <span>Editar Fatura</span>
                        </v-tooltip>
                        <v-tooltip left v-if="canDestroy(item)">
                            <template v-slot:activator="{ on }">
                                <div v-on="on">
                                    <form
                                        :action="deleteRoute(billClientId(item), item.id)"
                                        :id="`frmDelete${item.id}`"
                                        method="post"
                                    >
                                        <input type="hidden" name="_method" value="delete"/>
                                        <input type="hidden" name="reason" :value="destroyReason"/>
                                        <input type="hidden" name="cancel_invoice" :value="cancelInvoice ? 1 : 0"/>
                                        <cp-csrf-token/>
                                        <cp-icon-button
                                            title="Remover"
                                            icon="$trashCan"
                                            color="error"
                                            @click="deleteItem(item, $event)"
                                        >
                                        </cp-icon-button>
                                    </form>
                                </div>
                            </template>
                            <span>Remover</span>
                        </v-tooltip>
                        <ResendBill
                            v-if="canResendBill(item)"
                            :bill="item"
                            :client="client"
                            :route-name="resendBillRouteName"
                        />
                        </slot>
                    </div>
                </template>
                <template #footer>
                    <cp-pagination
                        v-if="pagination.last_page > 1"
                        :pagination="pagination"
                        :routeParam="paginationRouteParam"
                        :params="paginationParams"
                    />
                </template>
            </v-data-table>
        </template>
        <cp-simple-modal ref="simple-modal">
            <template #body>
                <v-card>
                    <v-card-text class="pa-4 black--text body-2">
                        <v-form ref="destroyForm">
                            <div>
                                {{ destroyMessage }}
                            </div>
                            <div>
                                <v-text-field
                                    label="Justificativa:"
                                    v-model="destroyReason"
                                    :rules="destroyReasonRules"
                                >
                                </v-text-field>
                            </div>
                            <div>
                                <v-radio-group
                                    v-model="cancelInvoice"
                                    label="Cancelar Nota Fiscal:"
                                    :rules="cancelInvoiceRules"
                                    row
                                >
                                    <v-radio
                                        label="Sim"
                                        :value="true"
                                    ></v-radio>
                                    <v-radio
                                        label="Não"
                                        :value="false"
                                    ></v-radio>
                                </v-radio-group>
                            </div>
                        </v-form>
                    </v-card-text>
                </v-card>
            </template>
            <template #actions>
                <cp-button
                    color="secondary"
                    @click="cancelDestroy"
                    :disabled="saving"
                    :loading="saving"
                >
                    Cancelar
                </cp-button>
                <cp-button
                    color="primary"
                    @click="confirmDestroy"
                    :disabled="saving"
                    :loading="saving"
                >
                    OK! Continuar
                </cp-button>
            </template>
        </cp-simple-modal>
    </cp-base-page>
</template>

<script>
import moment from 'moment'
import Client from '@cp-models-common/Client'
import FilterBills from '@core-components/filters/FilterBills'
import ResendBill from '@core-components/modal/ResendBill'

export default {
    props: {
        pageTitle: {
            type: String,
            default: 'Histórico de Faturas'
        },
        categorySubtitle: {
            type: String,
            default: 'Exibindo todas as faturas de planos, Mini CPU, Certificados, Multas'
        },
        listingTitle: {
            type: String,
            default: 'Faturas do cliente'
        },
        breadcrumbItems: {
            type: Array,
            required: true
        },
        clientData: {
            type: Object,
            default: null
        },
        billsData: {
            type: Object,
            default: null
        },
        paginationData: {
            type: Object,
            default: () => ({})
        },
        searchData: {
            type: String,
            default: null
        },
        filtersData: {
            type: Object,
            default: () => ({})
        },
        deleteRouteName: {
            type: String,
            default: 'v2.clients.bills.destroy'
        },
        indexRouteName: {
            type: String,
            default: 'v2.billing.bills.index'
        },
        showCreateBillButton: {
            type: Boolean,
            default: false
        },
        createBillRouteName: {
            type: String,
            default: ''
        },
        createBillRouteParams: {
            type: [Array, Object, String, Number],
            default: () => []
        },
        createBillButtonText: {
            type: String,
            default: 'Nova Fatura'
        },
        editBillRouteName: {
            type: String,
            default: ''
        },
        editPermission: {
            type: String,
            default: 'update bills'
        },
        destroyPermission: {
            type: String,
            default: 'destroy bills'
        },
        resendBillRouteName: {
            type: String,
            default: ''
        },
        resendPermission: {
            type: String,
            default: 'resend charges'
        },
        viewPermission: {
            type: String,
            default: 'show bills'
        },
        statusData: {
            type: String,
            default: null
        },
        billRoutesUseClientParam: {
            type: Boolean,
            default: true,
        },
    },

    data: () => ({
        client: null,
        bills: [],
        baseHeaders: [
            {text: 'Nº', value: 'external_id', sortable: false},
            {text: 'Cobrança', value: 'billingAt', sortable: false},
            {text: 'Vencimento', value: 'dueAt', sortable: false},
            {text: 'Total', value: 'originalPriceBr', sortable: false},
            {text: 'Tipo', value: 'billType', sortable: false},
            {text: 'Status', value: 'status', sortable: false},
            {text: 'Ciclo', value: 'bill_cycle', sortable: false},
            {text: 'Ações', value: 'actions', sortable: false},
        ],
        statusButtons: [
            { value: '', label: 'Todos', activeClass: 'btn-default', icon: null, param: { clear_status: 1 } },
            { value: 'open', label: 'Em Aberto', activeClass: 'btn-warning', icon: '$clock', param: { status: 'open' } },
            { value: 'late', label: 'Em Atraso', activeClass: 'btn-deleted', icon: '$clock', param: { status: 'late' } },
            { value: 'paid', label: 'Paga', activeClass: 'btn-success', icon: '$check', param: { status: 'paid' } },
            { value: 'canceled', label: 'Cancelada', activeClass: 'btn-deleted', icon: '$cancel', param: { status: 'canceled' } },
        ],
        pagination: {},
        search: null,
        filters: {},
        isFilterOpen: false,
        destroyMessage: '',
        destroyReason: '',
        saving: false,
        form: null,
        status: null,
        cancelInvoice: null,
        destroyReasonRules: [
            v => !!(v && String(v).trim()) || 'Justificativa é obrigatória.',
            v => !v || String(v).length <= 250 || 'Justificativa deve ter no máximo 250 caracteres.',
        ],
        cancelInvoiceRules: [
            v => typeof v === 'boolean' || 'Selecione se deseja cancelar a nota fiscal.',
        ],
    }),

    created() {
        this.status = this.statusData ?? null
    },

    metaInfo() {
        return {
            title: this.pageTitle
        }
    },

    components: {
        FilterBills,
        ResendBill,
    },

    watch: {
        clientData: {
            handler() {
                this.init()
            },
            immediate: true
        },
        billsData: {
            handler() {
                this.init()
            },
            immediate: true
        }
    },

    methods: {
        init() {
            this.setBreadcrumb(this.breadcrumbItems)
            this.pagination = this.paginationData || {}
            this.search = this.billsData ? this.searchData : null
            this.filters = this.billsData ? (this.filtersData ?? {}) : {}

            let rawBills = []

            if (this.clientData) {
                this.client = new Client(this.clientData)
                rawBills = this.clientData?.bills?.data ?? []
            } else {
                this.client = null
                rawBills = this.billsData?.data ?? []
            }

            this.bills = Array.isArray(rawBills) ? rawBills.map(bill => this.normalizeBill(bill)) : []
        },

        billClientId(item) {
            return this.client?.id ?? item.company?.id ?? null
        },

        indexRouteParams(extra = {}) {
            return {...extra}
        },

        normalizeBill(bill) {
            const statusData = this.buildStatusData(bill)

            return {
                ...bill,
                company: bill.company ? new Client(bill.company) : null,
                billingAt: bill.billing_at ? this.formatDate(bill.billing_at) : '',
                dueAt: bill.due_at ? this.formatDate(bill.due_at) : '',
                originalPriceBr: bill.original_price
                    ? this.formatCurrency(bill.original_price)
                    : '',
                billType: bill.subscription_id ? 'Mensalidade' : 'Fatura Avulsa',
                statusData,
            }
        },

        buildStatusData(bill) {
            let data = {
                color: 'grey lighten-2',
                textColor: 'black',
                icon: '$check',
                text: 'Em dia'
            }

            if (bill.paid_at !== null) {
                data = {
                    color: 'success',
                    textColor: 'white',
                    icon: '$check',
                    text: 'Paga'
                }
            }

            if (bill.due_at && moment(bill.due_at).isBefore(moment()) && bill.paid_at === null) {
                data = {
                    color: 'error',
                    textColor: 'white',
                    icon: '$clock',
                    text: 'Atrasada'
                }
            }

            if (bill.canceled_at !== null) {
                data = {
                    color: 'error',
                    textColor: 'white',
                    icon: '$cancel',
                    text: 'Cancelada'
                }
            }

            return data
        },

        formatDate(date = '') {
            return new Intl.DateTimeFormat('pt-BR', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            }).format(new Date(date))
        },

        formatCurrency(value = 0) {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }).format(value)
        },

        deleteRoute(clientId, billId) {
            if (this.billRoutesUseClientParam) {
                return this.route(this.deleteRouteName, [clientId, billId])
            }

            return this.route(this.deleteRouteName, {bill: billId})
        },

        editBillRoute(clientId, billId) {
            if (this.billRoutesUseClientParam) {
                return this.route(this.editBillRouteName, [clientId, billId])
            }

            return this.route(this.editBillRouteName, {bill: billId})
        },

        deleteItem(item, event) {
            event.preventDefault();
            this.form = `#frmDelete${item.id}`
            this.destroyReason = ''
            this.cancelInvoice = null
            this.destroyMessage = `Deseja realmente cancelar a fatura ${item.external_id}?`

            this.$refs['simple-modal'].open({
                title: 'Atenção!',
                persistent: true,
            })

            this.$nextTick(() => {
                this.$refs.destroyForm?.resetValidation()
            })
        },

        cancelDestroy() {
            this.form = null
            this.destroyMessage = ''
            this.destroyReason = ''
            this.cancelInvoice = null
            this.saving = false
            this.$refs.destroyForm?.resetValidation()
            this.$refs['simple-modal'].close()
        },

        confirmDestroy() {
            if (!this.$refs.destroyForm?.validate()) {
                return
            }

            this.saving = true
            document.querySelector(this.form).submit();
        },

        canDestroy(item) {
            return !item.paid_at && !item.canceled_at && this.can(this.destroyPermission)
        },

        canResendBill(item) {
            return !item.paid_at
                && !item.canceled_at
                && this.can(this.resendPermission)
                && this.resendBillRouteName
        },

        canEdit(item) {
            return !item.paid_at && !item.canceled_at && this.can(this.editPermission) && this.editBillRouteName
        },

        canView() {
            return this.can(this.viewPermission)
        },

        exportBills() {
            axios.get(this.route('v2.billing.bills.export', this.buildExportParams()))
            .then(() => {
                this.$bus.$emit('simple-modal', {
                    title: 'Exportando...',
                    message: 'Estamos processando seu relatório, ele será encaminhado para seu e-mail em breve.',
                    btnError: {hide: true},
                    btnSuccess: {color: 'primary', text: 'OK'},
                    persistent: true
                })

            }).catch((error) => {
                if (this.errorFeedback(error) == 422 || this.errorFeedback(error) == 400 || this.errorFeedback(error) == 404) {
                        let data = error.response.data;

                        if (typeof data === 'string') {
                            data = JSON.parse(data);
                        }

                        if (typeof data === 'object' && data !== null && data.message) {
                            this.$bus.$emit('feedback', {
                                message: data.message,
                                type: 'error',
                            });
                        }
                    } else {
                        this.$bus.$emit('feedback', {
                            message: 'Erro ao exportar faturas.',
                            type: 'error',
                        })
                    }
            })
        },

        buildExportParams() {
            const filters = _.pickBy(this.filters, (v) =>
                Array.isArray(v) ? _.compact(v).length : !_.isEmpty(String(v).trim())
            );

            const status = _.trim(this.status) || undefined;
            const hasFilters = !_.isEmpty(filters);

            return {
                search: this.search || undefined,
                filters: hasFilters ? filters : undefined,
                status,
                clear_filters: hasFilters ? undefined : 1,
                clear_status: status ? undefined : 1,
                clear_search: this.search ? undefined : 1,
            };
        },
    },


    computed: {
        headers() {
            if (this.clientData) {
                return this.baseHeaders
            }

            return [
                {text: 'Cliente', value: 'company', sortable: false},
                ...this.baseHeaders,
            ]
        },

        paginationRouteParam() {
            if (this.clientData) {
                return this.client?.id ?? this.clientData.id
            }

            return null
        },

        paginationParams() {
            let params = {}

            if (this.billsData && this.search) {
                params.search = this.search
            }

            if (this.billsData && this.status) {
                params.status = this.status
            }

            if (this.billsData && this.filters) {
                _.each(this.filters, (value, field) => {
                    if (Array.isArray(value)) {
                        _.each(value, (item, index) => {
                            params[`filters[${field}][${index}]`] = item
                        })
                    } else {
                        params[`filters[${field}]`] = value
                    }
                })
            }

            return params
        },

        showFilters() {
            return !!this.billsData
        },

        showHeaderActions() {
            if (!this.client) {
                return this.showCreateBillButton || !!this.$slots['header-actions']
            }

            return (this.showCreateBillButton || !!this.$slots['header-actions']) && this.client.address_id !== null
        },

        createBillRoute() {
            if (!this.createBillRouteName) {
                return '#'
            }

            if (Array.isArray(this.createBillRouteParams)) {
                return this.route(this.createBillRouteName, this.createBillRouteParams)
            }

            if (this.createBillRouteParams && typeof this.createBillRouteParams === 'object') {
                return this.route(this.createBillRouteName, this.createBillRouteParams)
            }

            if (this.createBillRouteParams !== '' && this.createBillRouteParams !== null && this.createBillRouteParams !== undefined) {
                return this.route(this.createBillRouteName, [this.createBillRouteParams])
            }

            return this.route(this.createBillRouteName)
        }
    },
}
</script>

<style>

</style>
